import { useEffect, useMemo, useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Chip from '@mui/material/Chip';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import FormControl from '@mui/material/FormControl';
import Grid from '@mui/material/Grid';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import Add from '@mui/icons-material/esm/Add';
import Build from '@mui/icons-material/esm/Build';
import EventAvailable from '@mui/icons-material/esm/EventAvailable';
import PrecisionManufacturing from '@mui/icons-material/esm/PrecisionManufacturing';
import WarningAmber from '@mui/icons-material/esm/WarningAmber';
import { DataTable, KPICard, Column } from '../../components/common';
import { PageHeader } from '../../components/layout';
import equipmentService, { EquipmentAsset, EquipmentAllocation, EquipmentStatus } from '../../services/api/equipmentService';
import resourcesService, { ResourceCatalogItem } from '../../services/api/resourcesService';

const statusLabel: Record<EquipmentStatus, string> = {
  available: 'Available',
  allocated: 'Allocated',
  maintenance: 'Maintenance',
  out_of_service: 'Out of service',
};

const statusColor = (status: EquipmentStatus): 'success' | 'info' | 'warning' | 'error' => {
  if (status === 'available') return 'success';
  if (status === 'allocated') return 'info';
  if (status === 'maintenance') return 'warning';
  return 'error';
};

const dateText = (value?: string | null) => value ? new Date(value).toLocaleDateString() : '-';

export default function EquipmentPage() {
  const [assets, setAssets] = useState<EquipmentAsset[]>([]);
  const [allocations, setAllocations] = useState<EquipmentAllocation[]>([]);
  const [maintenanceDue, setMaintenanceDue] = useState<EquipmentAsset[]>([]);
  const [summary, setSummary] = useState({
    totalAssets: 0,
    available: 0,
    allocated: 0,
    maintenance: 0,
    outOfService: 0,
    maintenanceDue: 0,
    utilizationPercent: 0,
  });
  const [catalog, setCatalog] = useState<ResourceCatalogItem[]>([]);
  const [statusFilter, setStatusFilter] = useState<'all' | EquipmentStatus>('all');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [assetDialogOpen, setAssetDialogOpen] = useState(false);
  const [allocationDialogOpen, setAllocationDialogOpen] = useState(false);
  const [assetForm, setAssetForm] = useState({
    assetCode: '',
    resourceId: '',
    serialNumber: '',
    status: 'available' as EquipmentStatus,
    ownership: 'owned',
    maintenanceDueDate: '',
    notes: '',
  });
  const [allocationForm, setAllocationForm] = useState({
    assetId: '',
    taskId: '',
    allocatedFrom: '',
    allocatedTo: '',
    notes: '',
  });

  const load = async () => {
    try {
      setLoading(true);
      setError(null);
      const [dashboard, resources] = await Promise.all([
        equipmentService.getDashboard(),
        resourcesService.list(),
      ]);
      setAssets(dashboard.assets);
      setAllocations(dashboard.currentAllocations);
      setMaintenanceDue(dashboard.maintenanceDue);
      setSummary(dashboard.summary);
      setCatalog(resources.filter((resource) => resource.resourceType === 'equipment'));
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load equipment dashboard');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const filteredAssets = useMemo(
    () => statusFilter === 'all' ? assets : assets.filter((asset) => asset.status === statusFilter),
    [assets, statusFilter]
  );

  const resetAssetForm = () => {
    setAssetForm({
      assetCode: '',
      resourceId: '',
      serialNumber: '',
      status: 'available',
      ownership: 'owned',
      maintenanceDueDate: '',
      notes: '',
    });
  };

  const createAsset = async () => {
    await equipmentService.createAsset({
      ...assetForm,
      resourceId: assetForm.resourceId || null,
      serialNumber: assetForm.serialNumber || null,
      maintenanceDueDate: assetForm.maintenanceDueDate || null,
      notes: assetForm.notes || null,
    });
    setAssetDialogOpen(false);
    resetAssetForm();
    await load();
  };

  const allocateAsset = async () => {
    await equipmentService.allocateAsset({
      ...allocationForm,
      notes: allocationForm.notes || null,
    });
    setAllocationDialogOpen(false);
    setAllocationForm({ assetId: '', taskId: '', allocatedFrom: '', allocatedTo: '', notes: '' });
    await load();
  };

  const columns: Column<EquipmentAsset>[] = [
    { id: 'assetCode', label: 'Asset', minWidth: 130 },
    {
      id: 'resource',
      label: 'Catalog Item',
      minWidth: 190,
      format: (_value, row) => row.resource ? `${row.resource.resourceCode} - ${row.resource.name}` : '-',
    },
    { id: 'serialNumber', label: 'Serial', minWidth: 130, format: (value) => value || '-' },
    {
      id: 'status',
      label: 'Status',
      minWidth: 130,
      format: (value: EquipmentStatus) => <Chip size="small" label={statusLabel[value]} color={statusColor(value)} />,
    },
    {
      id: 'currentLocation',
      label: 'Location',
      minWidth: 150,
      format: (_value, row) => row.currentLocation ? `${row.currentLocation.locationCode} - ${row.currentLocation.name}` : '-',
    },
    { id: 'maintenanceDueDate', label: 'Maintenance Due', minWidth: 140, format: (value) => dateText(value) },
    { id: 'ownership', label: 'Ownership', minWidth: 100, format: (value) => value === 'rented' ? 'Rented' : 'Owned' },
  ];

  const allocationColumns: Column<EquipmentAllocation>[] = [
    { id: 'asset', label: 'Asset', minWidth: 140, format: (_value, row) => row.asset?.assetCode || row.assetId },
    { id: 'task', label: 'Task', minWidth: 190, format: (_value, row) => row.task ? `${row.task.taskCode} - ${row.task.name}` : row.taskId },
    { id: 'allocatedFrom', label: 'From', minWidth: 130, format: (value) => new Date(value).toLocaleDateString() },
    { id: 'allocatedTo', label: 'To', minWidth: 130, format: (value) => new Date(value).toLocaleDateString() },
  ];

  return (
    <Box>
      <PageHeader
        title="Equipment"
        subtitle="Asset registry, task allocations, maintenance due, and utilization"
        actions={
          <Stack direction="row" spacing={1}>
            <Button startIcon={<EventAvailable />} variant="outlined" onClick={() => setAllocationDialogOpen(true)}>
              Allocate
            </Button>
            <Button startIcon={<Add />} variant="contained" onClick={() => setAssetDialogOpen(true)}>
              Asset
            </Button>
          </Stack>
        }
      />

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Assets" value={`${summary.totalAssets}`} icon={<PrecisionManufacturing />} color="primary" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Available" value={`${summary.available}`} icon={<EventAvailable />} color="success" />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Maintenance Due" value={`${summary.maintenanceDue}`} icon={<Build />} color={summary.maintenanceDue ? 'warning' : 'success'} />
        </Grid>
        <Grid item xs={12} sm={6} md={3}>
          <KPICard title="Utilization" value={`${summary.utilizationPercent}%`} icon={<WarningAmber />} color={summary.utilizationPercent > 85 ? 'warning' : 'info'} />
        </Grid>
      </Grid>

      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} sx={{ mb: 2 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select value={statusFilter} label="Status" onChange={(event) => setStatusFilter(event.target.value as any)}>
            <MenuItem value="all">All statuses</MenuItem>
            {Object.entries(statusLabel).map(([value, label]) => (
              <MenuItem key={value} value={value}>{label}</MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Registry</Typography>
            <DataTable columns={columns} data={filteredAssets} loading={loading} searchable pagination />
          </Box>
        </Grid>
        <Grid item xs={12} md={7}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Current Allocations</Typography>
            <DataTable columns={allocationColumns} data={allocations} loading={loading} pagination={false} />
          </Box>
        </Grid>
        <Grid item xs={12} md={5}>
          <Box sx={{ p: 2, bgcolor: 'background.paper', borderRadius: 1, boxShadow: 1 }}>
            <Typography variant="h6" sx={{ mb: 2 }}>Maintenance Due</Typography>
            <Stack spacing={1}>
              {maintenanceDue.length === 0 ? (
                <Alert severity="success">No assets due within 7 days.</Alert>
              ) : maintenanceDue.map((asset) => (
                <Box key={asset.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, borderBottom: '1px solid', borderColor: 'divider', py: 1 }}>
                  <Box>
                    <Typography variant="body2" fontWeight={700}>{asset.assetCode}</Typography>
                    <Typography variant="caption" color="text.secondary">{asset.resource?.name || 'Unlinked asset'}</Typography>
                  </Box>
                  <Chip size="small" color="warning" label={dateText(asset.maintenanceDueDate)} />
                </Box>
              ))}
            </Stack>
          </Box>
        </Grid>
      </Grid>

      <Dialog open={assetDialogOpen} onClose={() => setAssetDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>New Equipment Asset</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <TextField label="Asset code" value={assetForm.assetCode} onChange={(e) => setAssetForm({ ...assetForm, assetCode: e.target.value })} required />
          <FormControl>
            <InputLabel>Catalog item</InputLabel>
            <Select value={assetForm.resourceId} label="Catalog item" onChange={(e) => setAssetForm({ ...assetForm, resourceId: e.target.value })}>
              <MenuItem value="">Unlinked</MenuItem>
              {catalog.map((resource) => (
                <MenuItem key={resource.id} value={resource.id}>{resource.resourceCode} - {resource.name}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Serial number" value={assetForm.serialNumber} onChange={(e) => setAssetForm({ ...assetForm, serialNumber: e.target.value })} />
          <FormControl>
            <InputLabel>Status</InputLabel>
            <Select value={assetForm.status} label="Status" onChange={(e) => setAssetForm({ ...assetForm, status: e.target.value as EquipmentStatus })}>
              {Object.entries(statusLabel).map(([value, label]) => <MenuItem key={value} value={value}>{label}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField type="date" label="Maintenance due" InputLabelProps={{ shrink: true }} value={assetForm.maintenanceDueDate} onChange={(e) => setAssetForm({ ...assetForm, maintenanceDueDate: e.target.value })} />
          <TextField label="Notes" multiline minRows={2} value={assetForm.notes} onChange={(e) => setAssetForm({ ...assetForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAssetDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={createAsset} disabled={!assetForm.assetCode}>Create</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={allocationDialogOpen} onClose={() => setAllocationDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Allocate Equipment</DialogTitle>
        <DialogContent sx={{ display: 'grid', gap: 2, pt: 2 }}>
          <FormControl>
            <InputLabel>Asset</InputLabel>
            <Select value={allocationForm.assetId} label="Asset" onChange={(e) => setAllocationForm({ ...allocationForm, assetId: e.target.value })}>
              {assets.map((asset) => (
                <MenuItem key={asset.id} value={asset.id}>{asset.assetCode} - {statusLabel[asset.status]}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField label="Task ID" value={allocationForm.taskId} onChange={(e) => setAllocationForm({ ...allocationForm, taskId: e.target.value })} required />
          <TextField type="datetime-local" label="Allocated from" InputLabelProps={{ shrink: true }} value={allocationForm.allocatedFrom} onChange={(e) => setAllocationForm({ ...allocationForm, allocatedFrom: e.target.value })} />
          <TextField type="datetime-local" label="Allocated to" InputLabelProps={{ shrink: true }} value={allocationForm.allocatedTo} onChange={(e) => setAllocationForm({ ...allocationForm, allocatedTo: e.target.value })} />
          <TextField label="Notes" multiline minRows={2} value={allocationForm.notes} onChange={(e) => setAllocationForm({ ...allocationForm, notes: e.target.value })} />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAllocationDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={allocateAsset}
            disabled={!allocationForm.assetId || !allocationForm.taskId || !allocationForm.allocatedFrom || !allocationForm.allocatedTo}
          >
            Allocate
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
